import { getIdentity } from '@sdc/auth';
import { useEffect, useState } from 'react';

export type SessionWaitState = 'waiting' | 'ready' | 'none';

/**
 * Wartet darauf, dass supabase-js eine Session hergestellt hat (z. B. nachdem
 * Tokens aus der URL verarbeitet wurden) – mit hartem Timeout.
 *
 * Ohne Timeout bleibt die Seite im Spinner haengen, wenn die Initialisierung
 * nicht durchlaeuft. Genau das war der Fehler: leere Seite ohne jeden Hinweis.
 */
export function useSessionWait(timeoutMs = 8000): {
  state: SessionWaitState;
  email: string | null;
} {
  const [state, setState] = useState<SessionWaitState>('waiting');
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const identity = getIdentity();

    const settle = (session: { user: { email?: string } } | null) => {
      if (!active) return;
      if (session) {
        setEmail(session.user.email ?? null);
        setState('ready');
      }
    };

    const { data: sub } = identity.auth.onAuthStateChange((_event, session) => settle(session));

    // Zusaetzlich pollen: onAuthStateChange feuert nicht, wenn die Session schon
    // vor dem Mount stand.
    const poll = window.setInterval(() => {
      void identity.auth.getSession().then(({ data }) => settle(data.session));
    }, 400);

    void identity.auth.getSession().then(({ data }) => settle(data.session));

    const timer = window.setTimeout(() => {
      if (active) setState((current) => (current === 'waiting' ? 'none' : current));
    }, timeoutMs);

    return () => {
      active = false;
      sub.subscription.unsubscribe();
      window.clearInterval(poll);
      window.clearTimeout(timer);
    };
  }, [timeoutMs]);

  return { state, email };
}
